// DS: MO3.4.4
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import { USER_PROFILE_SHOP_SUB_PATHS, Url } from '@wix/da-url';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import PremiumGalleryIcon from '@wix/da-ds/pkg/Icons/16x16/PremiumGallery';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import ThumbStatusIndicators from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicators';
import { ThumbStatusTypes } from '@wix/da-ds/pkg/thumbs/types';
import { normalizePremiumFolderData } from '../../utils/normalizePremiumFolderData';
import FolderStats from '../../FolderCard/legos/FolderStats';
import { gallectionTypeToResourceType } from '../../types/gallection';
import ShopCardDumb from '../_partials/ShopCardDumb';
import PriceWithPoints from '../_partials/PriceWithPoints';

import s from './FolderShopCard.scss';

interface Props {
  width: number;
  folder: PapiGallection;
  withAuthorDetails?: boolean;
  withIndicators?: boolean;
  withUnlockButton?: boolean;
  linkToShop?: boolean;
  anchor?: boolean;
}

const FolderShopCard: React.FC<Props> = ({
  width,
  folder,
  withAuthorDetails,
  withIndicators = true,
  withUnlockButton,
  linkToShop = false,
  anchor = false,
}) => {
  const { t } = useTranslation();
  const { isPaidFolder, pointPrice, formattedDollarPrice } =
    normalizePremiumFolderData(folder?.premiumFolderData);

  let indicatorType: ThumbStatusTypes | null = null;
  if (isPaidFolder) {
    indicatorType = 'premium-folder';
  }

  return (
    <div className={s['root']} style={{ width }}>
      <BiLink
        className={s['link']}
        href={
          linkToShop
            ? Url.userShopLink(
                folder.owner.username,
                USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS,
                {},
                anchor
              )
            : Url.userGalleryLink({
                username: folder.owner.username,
                gallectionType: folder.type as any,
                folderId: folder.folderId,
                folderName: folder.name,
                anchor,
              })
        }
        biData={BiData({
          evid: 100,
          itemid: folder.folderId,
          typeid: gallectionTypeToResourceType(folder.type),
          sectionname: BiContextProvidedValue,
        })}
      >
        <VisuallyHidden>{folder.name}</VisuallyHidden>
      </BiLink>
      <ShopCardDumb
        width={width}
        coverImage={folder.thumb}
        title={folder.name}
        withAuthorDetails={withAuthorDetails}
        author={folder.owner}
        indicatorsSlot={
          withIndicators && indicatorType ? (
            <ThumbStatusIndicators
              className={s['indicators']}
              indicators={[
                {
                  type: indicatorType,
                  tooltip: t(`thumb.tooltip.${indicatorType}`),
                },
              ]}
            />
          ) : undefined
        }
        belowCoverImageSlot={<FolderStats folder={folder} />}
        detailsRightSlot={
          <PriceWithPoints
            formattedDollarsPrice={formattedDollarPrice}
            pointsPrice={pointPrice}
          />
        }
        footerSlot={
          withUnlockButton && (
            <div className={s['footer']}>
              <BuyButton
                size={'medium'}
                className={s['unlock-button']}
                variant="outline"
                rounded
                icon={PremiumGalleryIcon}
              >
                {t('shop.thumb.unlock_premium_gallery')}
              </BuyButton>
            </div>
          )
        }
      />
    </div>
  );
};
FolderShopCard.displayName = 'FolderShopCard';

export default FolderShopCard;
